/**
 * PPE list rows use the same on-disk layout as face images:
 * `/uploads/camera/{company_id}/{camera_number}/{filename}`
 *
 * The API may send `camera_name` (display label) while storage uses a short code
 * (often the prefix of `image_id` before `_YYYY_MM_DD_`).
 */

/** Matches edge-style names: `C21_2026_03_30_12_00_00_0.jpg`, `C1.2_2026_03_30_....jpg` */
const IMAGE_FILENAME_CAMERA_DATE = /^(.+?)_\d{4}_\d{2}_\d{2}_/;

export interface PpeDetectionImageRow {
  camera_number?: string | null;
  camera_name?: string | null;
  image_file?: string | null;
  image_id?: string | null;
}

function normalizeBasename(filename: string): string {
  const trimmed = filename.trim();
  if (!trimmed) return '';
  const parts = trimmed.split(/[/\\]/);
  return parts[parts.length - 1] ?? trimmed;
}

/**
 * Returns the storage folder segment (camera code) parsed from the filename, when present.
 */
export function extractCameraCodeFromPpeImageFilename(filename: string): string | null {
  const base = normalizeBasename(filename);
  if (!base) return null;
  const match = base.match(IMAGE_FILENAME_CAMERA_DATE);
  return match?.[1] ?? null;
}

export interface PpeImagePathSegments {
  cameraSegment: string;
  fileSegment: string;
}

/**
 * Resolves `{cameraSegment, fileSegment}` for `faceImageAPI.getImageUrl`.
 * Order: Prefer API `camera_number`; else derive from filename; else `camera_name`.
 */
export function resolvePpeImagePathSegments(row: PpeDetectionImageRow): PpeImagePathSegments | null {
  const fileSegment = String(row.image_file ?? row.image_id ?? '').trim();
  if (!fileSegment) return null;

  const fromApi = String(row.camera_number ?? '').trim();
  if (fromApi) {
    return { cameraSegment: fromApi, fileSegment };
  }

  const fromFilename = extractCameraCodeFromPpeImageFilename(fileSegment);
  if (fromFilename) {
    return { cameraSegment: fromFilename, fileSegment };
  }

  const fromLabel = String(row.camera_name ?? '').trim();
  if (fromLabel) {
    return { cameraSegment: fromLabel, fileSegment };
  }

  return null;
}
